import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Tag,
  Card,
  Row,
  Col,
  Tooltip,
  Avatar,
  Typography,
  Divider,
  Upload,
  UploadProps,
  Switch,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { RcFile } from "rc-upload/lib/interface";
import axios, { AxiosProgressEvent } from "axios";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  UserOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  UploadOutlined,
  YoutubeOutlined,
  LinkedinOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Title, Text } = Typography;

// ── Type Definitions ────────────────────────────────────────────────────────

interface StudentTestimonial {
  _id: string;
  name: string;
  designation?: string;
  location?: string;
  youtubeLink?: string;
  linkedinLink?: string;
  userId?: string;
  image?: string;
  about?: string;
  published?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface TestimonialFormValues {
  name: string;
  designation?: string;
  location?: string;
  youtubeLink?: string;
  linkedinLink?: string;
  image?: string;
  about?: string;
  published?: boolean;
}

interface FindResponse {
  data: StudentTestimonial[];
  totalItems: number;
  totalPages: number;
}

interface UploadResponse {
  filename: string;
  message?: string;
}

// ── API Configuration ───────────────────────────────────────────────────────

const API_BASE_URL: string = "https://service.ireedindia.com/v1";
const IMAGE_BASE_URL: string = API_BASE_URL.replace("/v1", "");

const StudentTestimonial: React.FC = () => {
  const [testimonials, setTestimonials] = useState<StudentTestimonial[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<StudentTestimonial | null>(null);
  const [viewingItem, setViewingItem] = useState<StudentTestimonial | null>(null);
  const [form] = Form.useForm<TestimonialFormValues>();
  const [searchText, setSearchText] = useState<string>("");
  const [filteredData, setFilteredData] = useState<StudentTestimonial[]>([]);
  const [publishFilter, setPublishFilter] = useState<"all" | "published" | "unpublished">("all");
  const [fileName, setFileName] = useState<string>("");
  const [uploading, setUploading] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Get user role from localStorage
  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem("userDetails");
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || "";
  };
  const userRole = getUserRole();

  // ── Upload Handler ──────────────────────────────────────────────────────

  const customUploadRequest: UploadProps["customRequest"] = async (options) => {
    const { file, onSuccess, onError, onProgress } = options;
    const formData = new FormData();
    formData.append("file", file as RcFile);
    try {
      const uploadUrl = `${IMAGE_BASE_URL}/upload`;
      const response = await axios.post<UploadResponse>(uploadUrl, formData, {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress?.({ percent });
          }
        },
      });
      onSuccess?.(response.data);
    } catch (error: any) {
      message.error("Upload failed");
      onError?.(error);
    }
  };

  const handleFileUpload: UploadProps["onChange"] = (info) => {
    if (info.file.status === "uploading") {
      setUploading(true);
      return;
    }
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      if (info.file.response?.filename) {
        setFileName(info.file.response.filename);
        form.setFieldsValue({ image: info.file.response.filename });
      } else {
        message.error("Upload succeeded but no filename received");
      }
      setUploading(false);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
      setUploading(false);
    }
  };

  const beforeUploadValidation: UploadProps["beforeUpload"] = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) message.error("You can only upload image files!");
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) message.error("Image must be smaller than 5MB!");
    return isImage && isLt5M;
  };

  // ── Data Fetching ────────────────────────────────────────────────────────

  const fetchTestimonials = async (): Promise<void> => {
    setLoading(true);
    try {
      const userDetailsString = localStorage.getItem("userDetails");
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const role = userDetails?.role;
      const userId = userDetails?.userId;

      let url = `${API_BASE_URL}/find-student-testimonial?pageNumber=0&size=1000&sortKey=createdAt&sortDir=DESC`;
      if (role !== "admin" && userId) {
        url += `&userId=${encodeURIComponent(userId)}`;
      }

      const response = await axios.get<FindResponse>(url);
      const { data = [], totalItems: total = 0 } = response.data;
      setTestimonials(data);
      setFilteredData(data);
      setTotalItems(total);
    } catch (error) {
      message.error("Failed to fetch student testimonials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    let filtered = testimonials.filter((item) =>
      ["name", "designation", "location"].some((field) =>
        (item[field as keyof StudentTestimonial] as string)
          ?.toLowerCase()
          .includes(searchText.toLowerCase())
      )
    );
    if (userRole === "admin" && publishFilter !== "all") {
      filtered = filtered.filter((item) => {
        const isPub = item.published ?? false;
        return publishFilter === "published" ? isPub : !isPub;
      });
    }
    setFilteredData(filtered);
  }, [searchText, testimonials, publishFilter, userRole]);

  // ── Form Submit ──────────────────────────────────────────────────────────

  const handleSubmit = async (values: TestimonialFormValues): Promise<void> => {
    setLoading(true);
    try {
      const userDetailsString = localStorage.getItem("userDetails");
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const userId = userDetails?.userId;

      const formData = {
        ...values,
        userId,
      };

      if (editingItem) {
        await axios.patch(`${API_BASE_URL}/student-testimonial/${editingItem._id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/create-student-testimonial`, formData);
      }
      message.success(`Testimonial ${editingItem ? "updated" : "created"} successfully`);
      setIsModalVisible(false);
      resetForm();
      await fetchTestimonials();
    } catch (error) {
      message.error(`Failed to ${editingItem ? "update" : "create"} testimonial`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    setEditingItem(null);
    setFileName("");
  };

  // ── CRUD Actions ─────────────────────────────────────────────────────────

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/student-testimonial/${id}`);
      message.success("Testimonial deleted successfully");
      await fetchTestimonials();
    } catch (error) {
      message.error("Failed to delete testimonial");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (id: string, currentStatus: boolean) => {
    setTogglingId(id);
    try {
      await axios.patch(`${API_BASE_URL}/student-testimonial/${id}`, { published: !currentStatus });
      message.success(`Testimonial ${!currentStatus ? "published" : "unpublished"} successfully`);
      await fetchTestimonials();
    } catch (error) {
      message.error("Error updating publish status");
    } finally {
      setTogglingId(null);
    }
  };

  // ── Modal Openers ────────────────────────────────────────────────────────

  const openCreateModal = () => {
    resetForm();
    form.setFieldsValue({ published: false });
    setIsModalVisible(true);
  };

  const openEditModal = (item: StudentTestimonial) => {
    setEditingItem(item);
    setFileName(item.image || "");
    form.setFieldsValue({
      name: item.name,
      designation: item.designation,
      location: item.location,
      youtubeLink: item.youtubeLink,
      linkedinLink: item.linkedinLink,
      image: item.image,
      about: item.about,
      published: item.published ?? false,
    });
    setIsModalVisible(true);
  };

  const openViewModal = (item: StudentTestimonial) => {
    setViewingItem(item);
    setIsViewModalVisible(true);
  };

  // ── Table Columns ────────────────────────────────────────────────────────

  const columns: ColumnsType<StudentTestimonial> = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 80,
      render: (image) => (
        <Avatar
          size={50}
          src={image ? `${IMAGE_BASE_URL}/image/${image}` : undefined}
          icon={<UserOutlined />}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      ellipsis: true,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      width: 180,
      render: (val) => (
        <>
          <IdcardOutlined className="mr-2" />
          {val || "N/A"}
        </>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 160,
      render: (val) => (
        <>
          <EnvironmentOutlined className="mr-2" />
          {val || "N/A"}
        </>
      ),
    },
    {
      title: "Links",
      key: "links",
      width: 110,
      render: (_, record) => (
        <Space size="small">
          {record.youtubeLink && (
            <a href={record.youtubeLink} target="_blank" rel="noopener noreferrer">
              <YoutubeOutlined style={{ fontSize: 18, color: "#ff0000" }} />
            </a>
          )}
          {record.linkedinLink && (
            <a href={record.linkedinLink} target="_blank" rel="noopener noreferrer">
              <LinkedinOutlined style={{ fontSize: 18, color: "#0a66c2" }} />
            </a>
          )}
          {!record.youtubeLink && !record.linkedinLink && "-"}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "published",
      key: "published",
      width: 130,
      render: (published: boolean) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: 600,
            backgroundColor: published ? "#d1fae5" : "#fef3c7",
            color: published ? "#065f46" : "#92400e",
          }}
        >
          {published ? "✓ Published" : "Draft"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 190,
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="View">
            <Button type="default" size="small" icon={<EyeOutlined />} onClick={() => openViewModal(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          </Tooltip>
          {userRole === "admin" && (
            <Tooltip title={record.published ? "Unpublish" : "Publish"}>
              <Button
                size="small"
                loading={togglingId === record._id}
                onClick={() => handlePublish(record._id, record.published || false)}
                style={{
                  backgroundColor: record.published ? "#faad14" : "#52c41a",
                  color: "#fff",
                  borderColor: "transparent",
                }}
              >
                {record.published ? "Unpub" : "Pub"}
              </Button>
            </Tooltip>
          )}
          {userRole === "admin" && (
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this testimonial?"
                onConfirm={() => record._id && handleDelete(record._id)}
                okText="Yes"
                cancelText="No"
                placement="left"
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="mb-6 shadow-lg border-0">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <Title level={2} className="!mb-1 text-gray-800">
                <TeamOutlined className="mr-3 text-blue-600" />
                Student Testimonials
              </Title>
              <Text type="secondary" className="text-base sm:text-lg">
                Manage student testimonial submissions
              </Text>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 border-0 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
            >
              Add Testimonial
            </Button>
          </div>
        </Card>

        {/* Search */}
        <Card className="mb-6 shadow-md border-0">
          <Input
            placeholder="Search by name, designation or location..."
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="large"
            className="rounded-lg"
          />
        </Card>

        {/* Stats */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={8}>
            <Card className="text-center shadow-md border-0 bg-gradient-to-r from-blue-400 to-blue-600 text-white">
              <div className="text-3xl font-bold">{totalItems}</div>
              <div className="text-blue-100">Total Testimonials</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card className="text-center shadow-md border-0 bg-gradient-to-r from-orange-400 to-orange-600 text-white">
              <div className="text-3xl font-bold">{testimonials.filter((t) => t.published).length}</div>
              <div className="text-orange-100">Published</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card className="text-center shadow-md border-0 bg-gradient-to-r from-purple-400 to-purple-600 text-white">
              <div className="text-3xl font-bold">{testimonials.filter((t) => !t.published).length}</div>
              <div className="text-purple-100">Drafts</div>
            </Card>
          </Col>
        </Row>

        {/* Table */}
        <Card className="shadow-lg border-0">
          {userRole === "admin" && (
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <span className="font-medium text-sm">Filter:</span>
              <select
                value={publishFilter}
                onChange={(e) => setPublishFilter(e.target.value as "all" | "published" | "unpublished")}
                className="px-3 py-1.5 rounded-md border border-gray-300 text-sm"
              >
                <option value="all">All</option>
                <option value="published">Published</option>
                <option value="unpublished">Unpublished</option>
              </select>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1100 }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} testimonials`,
              }}
              className="rounded-lg"
            />
          </div>
        </Card>

        {/* ── Create / Edit Modal ──────────────────────────────────────────── */}
        <Modal
          title={editingItem ? "Edit Testimonial" : "Create Testimonial"}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          width={700}
          className="top-4"
        >
          <div className="max-h-[80vh] overflow-y-auto p-2">
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              <Form.Item label="Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
                <Input size="large" placeholder="Enter student name" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Designation" name="designation">
                    <Input placeholder="e.g. Real Estate Consultant" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Location" name="location">
                    <Input placeholder="e.g. Jaipur, Rajasthan" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="YouTube Link"
                    name="youtubeLink"
                    rules={[{ type: "url", message: "Please enter a valid URL" }]}
                  >
                    <Input placeholder="https://youtube.com/..." prefix={<YoutubeOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="LinkedIn Link"
                    name="linkedinLink"
                    rules={[{ type: "url", message: "Please enter a valid URL" }]}
                  >
                    <Input placeholder="https://linkedin.com/in/..." prefix={<LinkedinOutlined />} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Photo" name="image">
                <Upload
                  listType="picture"
                  maxCount={1}
                  customRequest={customUploadRequest}
                  onChange={handleFileUpload}
                  beforeUpload={beforeUploadValidation}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    Upload Photo
                  </Button>
                </Upload>
                {fileName && (
                  <div className="mt-2 flex items-center gap-2">
                    <Avatar size={44} src={`${IMAGE_BASE_URL}/image/${fileName}`} icon={<UserOutlined />} />
                    <Text type="success">✅ Uploaded: {fileName}</Text>
                  </div>
                )}
              </Form.Item>

              <Form.Item label="About" name="about">
                <TextArea rows={4} placeholder="Short testimonial / about the student..." />
              </Form.Item>

              <Divider />

              <Form.Item label="Published" name="published" valuePropName="checked">
                <Switch checkedChildren="Published" unCheckedChildren="Draft" />
              </Form.Item>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading} className="bg-blue-600">
                  {editingItem ? "Update Testimonial" : "Create Testimonial"}
                </Button>
              </div>
            </Form>
          </div>
        </Modal>

        {/* ── View Modal ───────────────────────────────────────────────────── */}
        <Modal
          title="Testimonial Details"
          open={isViewModalVisible}
          onCancel={() => setIsViewModalVisible(false)}
          footer={<Button onClick={() => setIsViewModalVisible(false)}>Close</Button>}
          width={600}
        >
          {viewingItem && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Avatar
                  size={80}
                  src={viewingItem.image ? `${IMAGE_BASE_URL}/image/${viewingItem.image}` : undefined}
                  icon={<UserOutlined />}
                />
                <div className="flex-1">
                  <Title level={4} className="!mb-1">
                    {viewingItem.name}
                  </Title>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-gray-600 text-sm">
                    {viewingItem.designation && (
                      <span>
                        <IdcardOutlined /> {viewingItem.designation}
                      </span>
                    )}
                    {viewingItem.location && (
                      <span>
                        <EnvironmentOutlined /> {viewingItem.location}
                      </span>
                    )}
                  </div>
                  <div className="mt-2">
                    <Tag color={viewingItem.published ? "green" : "gold"}>
                      {viewingItem.published ? "Published" : "Draft"}
                    </Tag>
                  </div>
                  <div className="mt-2 flex gap-3">
                    {viewingItem.youtubeLink && (
                      <a href={viewingItem.youtubeLink} target="_blank" rel="noopener noreferrer">
                        <Tag color="red" icon={<YoutubeOutlined />}>
                          YouTube ↗
                        </Tag>
                      </a>
                    )}
                    {viewingItem.linkedinLink && (
                      <a href={viewingItem.linkedinLink} target="_blank" rel="noopener noreferrer">
                        <Tag color="blue" icon={<LinkedinOutlined />}>
                          LinkedIn ↗
                        </Tag>
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <Divider />
              {viewingItem.about && (
                <div>
                  <Text strong>About:</Text>
                  <p className="text-gray-700 mt-1 whitespace-pre-line">{viewingItem.about}</p>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default StudentTestimonial;
